import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  Box, Typography, Card, CardContent, CardActionArea,
  LinearProgress, Chip, useMediaQuery,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import TrendIndicator from './TrendIndicator';
import { MetricValueDisplay } from './styled';

const DashboardCard = ({
  title,
  value,
  unit,
  subtitle,
  icon: Icon,
  color = 'primary',
  status,
  trend,
  onClick,
  secondaryValue,
  chips,
  progress,
  disabled,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const statusIcon = useMemo(() => {
    if (!status?.severity) return null;
    switch (status.severity) {
      case 'optimal': case 'success': return <CheckCircleIcon sx={{ fontSize: isMobile ? 14 : 16 }} />;
      case 'warning': case 'moderate': return <WarningIcon sx={{ fontSize: isMobile ? 14 : 16 }} />;
      case 'error': case 'critical': return <ErrorOutlineIcon sx={{ fontSize: isMobile ? 14 : 16 }} />;
      default: return <InfoOutlinedIcon sx={{ fontSize: isMobile ? 14 : 16 }} />;
    }
  }, [status, isMobile]);

  if (disabled) {
    return (
      <Card variant="outlined" sx={{ height: '100%', bgcolor: alpha(theme.palette.action.disabledBackground, 0.3), borderStyle: 'dashed' }}>
        <CardContent sx={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 3 }}>
          <Typography variant="body2" color="text.disabled">{title}</Typography>
          <Typography variant="caption" color="text.disabled">No data available</Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card
      variant="outlined"
      sx={{
        height: '100%',
        borderColor: alpha(theme.palette[color].main, 0.3),
        transition: 'all 0.2s ease',
        cursor: onClick ? 'pointer' : 'default',
        '&:hover': onClick ? {
          borderColor: `${color}.main`,
          boxShadow: `0 4px 20px ${alpha(theme.palette[color].main, 0.15)}`,
          transform: 'translateY(-2px)',
        } : {},
      }}
    >
      <CardActionArea onClick={onClick} disabled={!onClick} sx={{ height: '100%' }}>
        <CardContent sx={{ height: '100%', p: { xs: 2, sm: 2.5 } }}>
          {/* Header */}
          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1.5, gap: 1 }}>
            <Box sx={{ p: { xs: 0.75, sm: 1 }, borderRadius: 2, bgcolor: alpha(theme.palette[color].main, 0.1), color: `${color}.main` }}>
              <Icon sx={{ fontSize: { xs: 18, sm: 20 } }} />
            </Box>
            {status && (
              <Chip
                icon={statusIcon}
                label={isMobile ? status.label?.split(' ')[0] : status.label}
                color={status.color || 'default'}
                size="small"
                variant="outlined"
                sx={{ fontWeight: 600, fontSize: { xs: '0.65rem', sm: '0.7rem' }, height: { xs: 24, sm: 28 } }}
              />
            )}
            {trend && <TrendIndicator direction={trend} />}
          </Box>

          {/* Title */}
          <Typography variant="body2" color="text.secondary" gutterBottom sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
            {title}
          </Typography>

          {/* Value */}
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5, flexWrap: 'wrap' }}>
            <MetricValueDisplay sx={{ fontSize: { xs: '1.5rem', sm: '2rem' } }}>
              {value ?? '—'}
            </MetricValueDisplay>
            {unit && (
              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                {unit}
              </Typography>
            )}
          </Box>

          {/* Secondary Info */}
          {secondaryValue && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
              {secondaryValue}
            </Typography>
          )}

          {/* Subtitle */}
          {subtitle && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
              {subtitle}
            </Typography>
          )}

          {/* Progress Bar */}
          {progress !== undefined && (
            <Box sx={{ mt: 1.5 }}>
              <LinearProgress
                variant="determinate"
                value={Math.min(progress, 100)}
                color={color}
                sx={{ height: 6, borderRadius: 1 }}
              />
            </Box>
          )}

          {/* Chips */}
          {chips && chips.length > 0 && (
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 1.5 }}>
              {chips.map((chip, idx) => (
                <Chip key={idx} {...chip} size="small" sx={{ height: 22, fontSize: '0.65rem', ...chip.sx }} />
              ))}
            </Box>
          )}
        </CardContent>
      </CardActionArea>
    </Card>
  );
};

DashboardCard.propTypes = {
  title: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  unit: PropTypes.string,
  subtitle: PropTypes.string,
  icon: PropTypes.elementType.isRequired,
  color: PropTypes.string,
  status: PropTypes.object,
  trend: PropTypes.string,
  onClick: PropTypes.func,
  secondaryValue: PropTypes.string,
  chips: PropTypes.array,
  progress: PropTypes.number,
  disabled: PropTypes.bool,
};

export default DashboardCard;